import { useEffect, useState } from "react";
import { Search, SlidersHorizontal, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatDate, formatNumber, imgSrc } from "@/lib/format";
import { EmptyRow, LoadingRow, PageHeader, Pager } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import StockAdjustDialog from "@/components/stock/StockAdjustDialog";

const PAGE_SIZE = 50;
export const CriticalBadge = () => <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200" data-testid="critical-badge"><AlertTriangle className="h-3 w-3" />Kritik</span>;

function LevelsTab({ warehouses, onAdjust, reload }) {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [wh, setWh] = useState("all");
  const [crit, setCrit] = useState(false);
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  useEffect(() => {
    setRes(null);
    const t = setTimeout(() => api.get("/stock", { params: { q, warehouse_id: wh === "all" ? "" : wh, critical_only: crit, page, page_size: PAGE_SIZE } }).then(({ data }) => setRes(data)), 250);
    return () => clearTimeout(t);
  }, [q, wh, crit, page, reload]);
  const canEdit = can("stok", "edit");
  return (
    <div className="panel overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 border-b border-slate-200">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-8 h-9" placeholder="Ürün, SKU, barkod..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="stock-search-input" />
        </div>
        <Select value={wh} onValueChange={(v) => { setWh(v); setPage(1); }}>
          <SelectTrigger className="h-9 sm:w-52" data-testid="stock-warehouse-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm depolar</SelectItem>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-[13px] text-slate-600"><Switch checked={crit} onCheckedChange={(v) => { setCrit(v); setPage(1); }} data-testid="stock-critical-toggle" />Sadece kritik</label>
      </div>
      <div className="overflow-x-auto max-h-[calc(100vh-320px)]">
        <table className="erp-table" data-testid="stock-table">
          <thead><tr><th></th><th>SKU</th><th>Ürün</th><th>Depo</th><th>Raf</th><th className="text-right">Mevcut</th><th className="text-right">Rezerve</th><th className="text-right">Kullanılabilir</th><th className="text-right" title="Ürün toplamı: limana ulaşmamış ithalat + yoldaki satın alma">Yolda</th><th className="text-right" title="Ürün toplamı: limana ulaşmış, alınmamış ithalat">Gümrükte</th><th className="text-right" title="Mevcut + Yolda + Gümrükte (satışa açık değil)">Toplam Potansiyel</th><th className="text-right">Min.</th><th>Durum</th><th></th></tr></thead>
          <tbody>
            {!res ? <LoadingRow cols={14} /> : res.items.length === 0 ? <EmptyRow cols={14} /> : res.items.map((r) => (
              <tr key={r.id} data-testid={`stock-row-${r.sku}-${r.warehouse_id}`}>
                <td><img src={imgSrc(r.image_url)} alt="" className="h-8 w-8 rounded object-cover border border-slate-200" /></td>
                <td className="font-mono text-xs font-medium text-slate-900">{r.sku}</td>
                <td className="max-w-[240px] truncate" title={r.name}>{r.name}</td>
                <td>{r.warehouse_name}</td>
                <td className="font-mono text-xs">{r.raf}</td>
                <td className="text-right font-mono" data-testid="stock-physical">{formatNumber(r.physical)}</td>
                <td className="text-right font-mono text-amber-700">{formatNumber(r.reserved)}</td>
                <td className="text-right font-mono font-semibold text-slate-900" data-testid="stock-available">{formatNumber(r.available)}</td>
                <td className="text-right font-mono text-blue-700" data-testid="stock-yolda">{formatNumber(r.yolda)}</td>
                <td className="text-right font-mono text-violet-700" data-testid="stock-gumrukte">{formatNumber(r.gumrukte)}</td>
                <td className="text-right font-mono text-slate-600" data-testid="stock-potential">{formatNumber(r.toplam_potansiyel)}</td>
                <td className="text-right font-mono text-slate-500">{formatNumber(r.min_stock)}</td>
                <td>{r.is_critical ? <CriticalBadge /> : <span className="text-[11px] text-emerald-700">Yeterli</span>}</td>
                <td className="text-right">{canEdit && <Button size="sm" variant="outline" className="h-7" onClick={() => onAdjust(r)} data-testid={`stock-adjust-${r.sku}-${r.warehouse_id}`}><SlidersHorizontal className="h-3.5 w-3.5 mr-1" />Düzelt</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {res && <Pager page={page} pageSize={PAGE_SIZE} total={res.total} onPage={setPage} testid="stock-pager" />}
    </div>
  );
}

function CriticalTab({ reload }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/stock/critical").then(({ data }) => setRows(data)); }, [reload]);
  return (
    <div className="panel overflow-x-auto">
      <table className="erp-table" data-testid="critical-table">
        <thead><tr><th>SKU</th><th>Ürün</th><th>Tedarikçi</th><th className="text-right">Fiziki</th><th className="text-right">Rezerve</th><th className="text-right">Kullanılabilir</th><th className="text-right">Min.</th><th className="text-right">Eksik</th><th className="text-right">Yolda</th></tr></thead>
        <tbody>
          {!rows ? <LoadingRow cols={9} /> : rows.length === 0 ? <EmptyRow cols={9} text="Kritik stokta ürün yok" /> : rows.map((r) => (
            <tr key={r.product_id} data-testid={`critical-row-${r.sku}`}>
              <td className="font-mono text-xs font-medium">{r.sku}</td>
              <td className="max-w-[260px] truncate">{r.name}</td>
              <td className="max-w-[200px] truncate">{r.supplier_name}</td>
              <td className="text-right font-mono">{formatNumber(r.physical)}</td>
              <td className="text-right font-mono text-amber-700">{formatNumber(r.reserved)}</td>
              <td className="text-right font-mono font-semibold text-rose-600">{formatNumber(r.available)}</td>
              <td className="text-right font-mono">{formatNumber(r.min_stock)}</td>
              <td className="text-right font-mono font-semibold text-rose-700">{formatNumber(r.shortage)}</td>
              <td className="text-right font-mono text-blue-700">{formatNumber(r.in_transit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MovementsTab({ reload }) {
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  useEffect(() => { api.get("/stock/movements", { params: { page, page_size: PAGE_SIZE } }).then(({ data }) => setRes(data)); }, [page, reload]);
  return (
    <div className="panel overflow-hidden">
      <div className="overflow-x-auto">
        <table className="erp-table" data-testid="movements-table">
          <thead><tr><th>Tarih</th><th>SKU</th><th>Ürün</th><th>Depo</th><th>Tür</th><th className="text-right">Önce</th><th className="text-right">Değişim</th><th className="text-right">Sonra</th><th>Neden</th><th>Kullanıcı</th></tr></thead>
          <tbody>
            {!res ? <LoadingRow cols={10} /> : res.items.length === 0 ? <EmptyRow cols={10} text="Henüz stok hareketi yok" /> : res.items.map((m) => (
              <tr key={m.id} data-testid="movement-row">
                <td className="font-mono text-xs">{formatDate(m.created_at)}</td>
                <td className="font-mono text-xs">{m.sku}</td>
                <td className="max-w-[220px] truncate">{m.product_name}</td>
                <td>{m.warehouse_name}</td>
                <td><span className="text-[11px] px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200">{m.type === "duzeltme" ? "Düzeltme" : m.type}</span></td>
                <td className="text-right font-mono">{formatNumber(m.before)}</td>
                <td className={`text-right font-mono font-semibold ${m.qty_delta > 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="movement-delta">{m.qty_delta > 0 ? "+" : ""}{formatNumber(m.qty_delta)}</td>
                <td className="text-right font-mono">{formatNumber(m.after)}</td>
                <td className="max-w-[220px] truncate" title={m.note}>{m.reason}{m.note ? ` – ${m.note}` : ""}</td>
                <td>{m.user_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {res && <Pager page={page} pageSize={PAGE_SIZE} total={res.total} onPage={setPage} testid="movements-pager" />}
    </div>
  );
}

export default function Stock() {
  const [warehouses, setWarehouses] = useState([]);
  const [adjusting, setAdjusting] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => { api.get("/stock/warehouses").then(({ data }) => setWarehouses(data)); }, []);
  const done = () => { setAdjusting(null); setReload((r) => r + 1); window.dispatchEvent(new Event("stock:changed")); };
  return (
    <div data-testid="stock-page">
      <PageHeader title="Stok" subtitle="Depo bazında fiziki, rezerve, kullanılabilir ve yoldaki stok" testid="stock-title" />
      <Tabs defaultValue="levels">
        <TabsList className="mb-4">
          <TabsTrigger value="levels" data-testid="tab-stock-levels">Stok Durumu</TabsTrigger>
          <TabsTrigger value="critical" data-testid="tab-stock-critical">Kritik Stok</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-stock-movements">Stok Hareketleri</TabsTrigger>
        </TabsList>
        <TabsContent value="levels"><LevelsTab warehouses={warehouses} onAdjust={setAdjusting} reload={reload} /></TabsContent>
        <TabsContent value="critical"><CriticalTab reload={reload} /></TabsContent>
        <TabsContent value="movements"><MovementsTab reload={reload} /></TabsContent>
      </Tabs>
      {adjusting && <StockAdjustDialog row={adjusting} onClose={() => setAdjusting(null)} onDone={done} />}
    </div>
  );
}
